import { useEffect, useState } from 'react';
import ProductBand from '@v2/shared/components/ProductBand';
import { Product } from '@v2/shared/lib/types';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { wafFetch } from '@v2/shared/lib/wafFetch';

/** Fewer than this and there is no band — what every legacy search band asked for. */
const MINIMUM = 4;

export type RecommendationBandProps = {
  /** The question the recommender is asked; see `ListRecommendationsRequest`. */
  type: 'also_viewed' | 'also_bought' | 'best_selling';
  /** The slug of the product `also_viewed` and `also_bought` are about. */
  product?: string;
  /** The slug of the category `best_selling` is about. */
  category?: string;
  title: string;
  /** Passed to the band; see ProductBand. */
  list?: string;
  /** Passed to the band, and decides the "Save X%" line too; see ProductBand. */
  showMsrp?: boolean;
};

/**
 * A band of recommended products — what the legacy `recommendations` js-module drew,
 * from `/services_api/storefront/v1/recommendations`, which answers with the products
 * rather than with markup.
 *
 * Asked for in the browser once the page is up, as the legacy module did, so nothing
 * waits on the recommender and the server renders nothing in its place. It stays that
 * way unless enough come back to fill a band.
 *
 * The cards carry the "Save X%" line the home page's bands do, wherever they carry the
 * cover price it is worked out from: a page that hides the cover price hides the saving
 * with it, as the magazine page does with its "You save" line (`layouts/product_v2.html`).
 */
export default function RecommendationBand({
  type,
  product,
  category,
  title,
  list,
  showMsrp = true,
}: RecommendationBandProps) {
  const { buildUrl } = useUrlGenerator();
  const [products, setProducts] = useState<Product[]>([]);

  const query = new URLSearchParams({ type });

  if (product) {
    query.set('product', product);
  }

  if (category) {
    query.set('category', category);
  }

  // Through buildUrl, so the AU storefront asks its own catalogue rather than the US one.
  const url = buildUrl(`/services_api/storefront/v1/recommendations.json?${query}`);

  useEffect(() => {
    let cancelled = false;

    wafFetch(url)
      .then((response) => (response.ok ? response.json() : []))
      .catch(() => [])
      .then((recommended: Product[]) => {
        if (!cancelled) {
          setProducts(recommended);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // ProductBand draws no card for a product without a base sku, so only the ones it will
  // draw count towards the minimum.
  const drawn = products.filter((product) => product.base_sku);

  if (drawn.length < MINIMUM) {
    return null;
  }

  return <ProductBand title={title} list={list} products={drawn} showSavings={showMsrp} showMsrp={showMsrp} />;
}
